import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, today } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Receiving stock, on its own page rather than in a dialog.
 *
 * A receipt is a multi-line document — the same shape as an invoice — and a
 * dialog gave it no room: lines were cramped, long catalogues were hard to
 * pick from, and a mistyped cost meant losing the lot on a stray click
 * outside. The page gets a URL you can land on, a browser back button that
 * does the obvious thing, and a guard before you walk away from typed lines.
 */
const blankLine = () => ({
  item_id: "", quantity: 1, unit_cost: 0, batch_number: "", expiry_date: "", notes: "",
});

export default function StockReceipt() {
  const navigate = useNavigate();
  const canEdit = useCanEdit();

  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    warehouse_id: "", party_id: "", reference_number: "",
    receipt_date: today(), notes: "", lines: [blankLine()],
  });

  useEffect(() => {
    api.get("/inventory/warehouses").then(({ data }) => {
      setWarehouses(data);
      setForm((f) => (f.warehouse_id ? f : {
        ...f,
        warehouse_id: data.find((w) => w.is_default)?.id || data[0]?.id || "",
      }));
    });
    api.get("/items").then(({ data }) => setItems(data));
    api.get("/parties", { params: { kind: "SUPPLIER" } })
      .then(({ data }) => setParties(data)).catch(() => setParties([]));
  }, []);

  const setLine = (idx, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const removeLine = (idx) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const filled = form.lines.filter((l) => l.item_id && Number(l.quantity) > 0);
  const total = form.lines.reduce(
    (t, l) => t + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);

  // Anything typed is worth a confirm before it is thrown away — the dialog
  // used to lose it to a click on the backdrop.
  const dirty = form.lines.some((l) => l.item_id || Number(l.quantity) !== 1
    || Number(l.unit_cost) || l.batch_number || l.expiry_date)
    || !!form.reference_number || !!form.notes || !!form.party_id;

  const leave = useCallback(() => {
    if (dirty && !window.confirm("Leave without recording this receipt? Your lines will be lost."))
      return;
    navigate("/inventory?tab=receipts");
  }, [dirty, navigate]);

  const save = async () => {
    if (!filled.length) {
      toast.error("Add at least one line with an item and quantity");
      return;
    }
    const missingBatch = filled.find((l) => {
      const item = items.find((i) => i.id === l.item_id);
      return item?.track_batches && !l.batch_number;
    });
    if (missingBatch) {
      const item = items.find((i) => i.id === missingBatch.item_id);
      toast.error(`${item.name} tracks batches — give this line a batch number`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/receipts", {
        ...form,
        lines: filled.map((l) => ({
          item_id: l.item_id, quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost) || 0,
          batch_number: l.batch_number || null,
          expiry_date: l.expiry_date || null, notes: l.notes,
        })),
      });
      toast.success(`${data.receipt_number} recorded — stock and cost updated`);
      navigate("/inventory?tab=receipts");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="space-y-4" data-testid="stock-receipt-page">
        <button onClick={() => navigate("/inventory")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft size={15} /> Inventory
        </button>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-800">
          Your role can view stock but not change it. Ask an owner to record this receipt.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-28" data-testid="stock-receipt-page">
      <div>
        <button onClick={leave} data-testid="receipt-back"
          className="-ml-2 inline-flex min-h-[40px] items-center gap-1.5 rounded-md px-2 text-sm text-slate-500 transition-colors duration-150 hover:text-slate-800">
          <ArrowLeft size={15} /> Inventory
        </button>
        <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
          Receive stock
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          Raises stock and blends the unit cost into each item&rsquo;s moving average. Record what
          the supplier actually delivered — a purchase bill on its own does not move stock.
        </p>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Warehouse</Label>
            <Select value={form.warehouse_id}
              onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v }))}>
              <SelectTrigger data-testid="receipt-warehouse"><SelectValue placeholder="Default" /></SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Supplier (optional)</Label>
            <Select value={form.party_id || "none"}
              onValueChange={(v) => setForm((f) => ({ ...f, party_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="receipt-party"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Date</Label>
            <Input type="date" data-testid="receipt-date" value={form.receipt_date}
              onChange={(e) => setForm((f) => ({ ...f, receipt_date: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Supplier bill no.</Label>
            <Input data-testid="receipt-reference" className="font-mono" value={form.reference_number}
              onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-600">Lines</h2>
          <Button size="sm" variant="outline" className="h-9 gap-1 text-xs sm:h-7"
            data-testid="receipt-add-line" onClick={addLine}>
            <Plus size={13} /> Add line
          </Button>
        </div>

        <div className="space-y-2 p-3">
          {form.lines.map((l, idx) => {
            const item = items.find((i) => i.id === l.item_id);
            const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
            return (
              <div key={idx} data-testid={`receipt-line-${idx}`}
                className="grid gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-12">
                <div className="space-y-1 sm:col-span-4">
                  <Label className="text-[10px] uppercase text-slate-500">Item</Label>
                  <Select value={l.item_id} onValueChange={(v) => setLine(idx, { item_id: v })}>
                    <SelectTrigger className="h-9" data-testid={`receipt-item-${idx}`}>
                      <SelectValue placeholder="Pick item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name}{i.scan_code ? ` · ${i.scan_code}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">
                    Qty{item ? ` (${item.unit_symbol})` : ""}
                  </Label>
                  <Input className="h-9 text-right font-mono" inputMode="decimal"
                    data-testid={`receipt-qty-${idx}`} value={l.quantity}
                    onChange={(e) => setLine(idx, { quantity: e.target.value })} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Unit cost</Label>
                  <Input className="h-9 text-right font-mono" inputMode="decimal"
                    data-testid={`receipt-cost-${idx}`} value={l.unit_cost}
                    onChange={(e) => setLine(idx, { unit_cost: e.target.value })} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">
                    Batch{item?.track_batches ? " *" : ""}
                  </Label>
                  <Input className="h-9 font-mono" data-testid={`receipt-batch-${idx}`}
                    value={l.batch_number}
                    onChange={(e) => setLine(idx, { batch_number: e.target.value })} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Expiry</Label>
                  <div className="flex gap-1">
                    <Input type="date" className="h-9" data-testid={`receipt-expiry-${idx}`}
                      value={l.expiry_date}
                      onChange={(e) => setLine(idx, { expiry_date: e.target.value })} />
                    {form.lines.length > 1 && (
                      <button className="shrink-0 text-slate-400 transition-colors duration-150 hover:text-rose-600"
                        data-testid={`receipt-remove-${idx}`} onClick={() => removeLine(idx)}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
                {lineTotal > 0 && (
                  <div className="text-right text-[11px] text-slate-500 sm:col-span-12">
                    Line total <span className="font-mono font-semibold text-slate-700">{inr(lineTotal)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Notes</Label>
        <Textarea rows={2} data-testid="receipt-notes" value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      </div>

      {/* Fixed, so the total and the action stay reachable however long the
          list of lines gets. */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-slate-500">
              {filled.length} line{filled.length === 1 ? "" : "s"} · Total{" "}
            </span>
            <span className="font-mono text-base font-bold tabular-nums" data-testid="receipt-total">
              {inr(total)}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={leave} data-testid="receipt-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !filled.length} data-testid="receipt-save"
              className="bg-blue-600 hover:bg-blue-700">
              {busy ? "Recording…" : "Record receipt"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
